<template>
	<div>
		<v-dialog
			scrollable
			transition="dialog-bottom-transition"
			:value="isVisible"
			max-width="800px"
			persistent
			@click:outside="handleClickOutside"
			@keydown.esc="handleClickOutside"
		>
			<v-card>
				<v-card-title style="display: block; padding: 0; font-size: 1rem">
					<v-stepper alt-labels v-model="stepperStep" flat>
						<v-stepper-header>
							<v-stepper-step :step="1" :complete="stepperStep > 1">
								{{ $t('label.select-job') }}
							</v-stepper-step>

							<v-divider></v-divider>

							<v-stepper-step :step="2" :complete="stepperStep > 2">
								{{ $t('label.upload-file') }}
							</v-stepper-step>

							<v-divider></v-divider>

							<v-stepper-step :step="3" :complete="stepperStep > 2">
								{{ $t('label.success') }}
							</v-stepper-step>
						</v-stepper-header>
					</v-stepper>
				</v-card-title>

				<v-divider></v-divider>

				<v-card-text style="padding: 0 0.5rem">
					<v-stepper v-model="stepperStep" flat>
						<v-stepper-items>
							<!-- STEP 1: Select Job -->
							<v-stepper-content :step="1">
								<ValidationObserver
									ref="bulk_import_form_step_1"
									tag="form"
									@submit="handleGoToSecondStep"
									id="bulk_import_form_step_1"
								>
									<JobPicker
										v-model="jobId"
										:jobs="jobs"
										:label="$t('label.jobs')"
										hide-inactive-teams
										hide-inactive-departments
										hide-draft-jobs
										:hide-closed-jobs="false"
										dense
										rules="required"
									></JobPicker>
								</ValidationObserver>
							</v-stepper-content>

							<!-- STEP 2: Upload File -->
							<v-stepper-content :step="2">
								<Dropzone
									v-if="!hasErrors"
									ref="bulkImportDropzone"
									id="bulkCandidateImportDropzone"
									:options="{
										maxFilesize: 5,
										maxFiles: 1,
										acceptedFiles: '.xlsx',
										addRemoveLinks: false,
									}"
									:initialFiles="[]"
									:onSuccess="handleDropzoneSuccess"
								></Dropzone>

								<!-- Validation Errors -->
								<v-alert
									v-if="validationErrors.length > 0"
									type="error"
									outlined
									class="mt-4"
								>
									<div class="font-weight-bold mb-2">
										{{ $t('text.validation-errors-found') }}
									</div>
									<ul class="pl-4 mb-3">
										<li v-for="(error, index) in validationErrors" :key="index">
											{{ error }}
										</li>
									</ul>
									<div class="text-body-2">
										{{ $t('text.fix-and-retry') }}
									</div>
								</v-alert>

								<!-- API Error -->
								<v-alert v-if="apiError" type="error" outlined class="mt-4">
									{{ apiError }}
								</v-alert>
							</v-stepper-content>

							<!-- STEP 3: Results -->
							<v-stepper-content :step="3">
								<v-alert outlined type="success" text>
									{{ $t('text.candidates-imported', { count: importedCount }) }}
								</v-alert>
							</v-stepper-content>
						</v-stepper-items>
					</v-stepper>
				</v-card-text>

				<v-divider></v-divider>

				<v-card-actions>
					<!-- Template Download Button -->
					<v-btn
						href="https://cdn.talentics.app/assets/bulk-import-template/bulk-import-template.xlsx"
						target="_blank"
						v-if="stepperStep === 1 || stepperStep === 2"
						small
					>
						{{ $t('button.download-excel-template') }}
					</v-btn>
					<v-spacer></v-spacer>

					<!-- Step 1 Actions -->
					<v-btn text @click="handleClose" v-if="stepperStep === 1">
						{{ $t('button.close') }}
					</v-btn>
					<v-btn
						color="primary"
						@click="handleGoToSecondStep"
						v-if="stepperStep === 1"
					>
						{{ $t('button.next') }}
					</v-btn>

					<!-- Step 2 Actions -->
					<v-btn
						text
						@click="handleBackToStepOne"
						v-if="stepperStep === 2 && !hasErrors"
					>
						<v-icon left>arrow_back</v-icon>
						{{ $t('button.back') }}
					</v-btn>

					<!-- Step 2 Error Actions -->
					<v-btn
						text
						@click="handleClose"
						v-if="stepperStep === 2 && hasErrors"
					>
						{{ $t('button.close') }}
					</v-btn>
					<v-btn
						color="primary"
						@click="handleRetry"
						v-if="stepperStep === 2 && hasErrors"
					>
						{{ $t('button.try-again') }}
					</v-btn>

					<!-- Step 3 Actions -->
					<v-btn text @click="handleCloseAfterSuccess" v-if="stepperStep === 3">
						{{ $t('button.close') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import Dropzone from '@/components/shared/dropzone/Dropzone';
import JobPicker from '@/components/shared/jobPicker/JobPicker';
import { ValidationObserver } from 'vee-validate';
import api from '@/api';

export default {
	name: 'BulkCandidateImport',
	computed: {
		...mapState({
			currentLang: state => state.currentUser.lang,
			isDark: state => state.currentUser.isDark,
			jobs: state => state.jobs.items,
		}),
		hasErrors() {
			return this.validationErrors.length > 0 || this.apiError !== null;
		},
	},
	components: {
		Dropzone,
		JobPicker,
		ValidationObserver,
	},
	data() {
		return {
			isVisible: false,
			fileId: null,
			jobId: null,
			stepperStep: 1,
			validationErrors: [],
			apiError: null,
			importedCount: 0,
		};
	},
	methods: {
		handleClickOutside(evt) {
			evt.preventDefault();
		},
		handleClose() {
			const { currentLang } = this;
			const { query } = this.$route;
			this.$router.push({
				path: `/${currentLang}/candidates`,
				query: {
					...query,
				},
			});
		},
		handleCloseAfterSuccess() {
			const { currentLang } = this;
			this.$router.push({
				path: `/${currentLang}/candidates`,
				query: {
					is_lead: 1,
				},
			});
		},
		handleBackToStepOne() {
			this.resetState();
			this.stepperStep = 1;
		},
		handleRetry() {
			this.resetState();
			// Stay on step 2 to allow re-upload
		},
		resetState() {
			this.validationErrors = [];
			this.apiError = null;
			this.fileId = null;
			this.importedCount = 0;
			// Clear Dropzone files
			this.$nextTick(() => {
				if (this.$refs.bulkImportDropzone?.$refs?.bulkCandidateImportDropzone) {
					this.$refs.bulkImportDropzone.$refs.bulkCandidateImportDropzone.removeAllFiles(
						true,
					);
				}
			});
		},
		async handleDropzoneSuccess(file, response) {
			const { jobId } = this;
			const { items } = response;
			const { id: fileId } = items[0];

			this.fileId = fileId;
			this.validationErrors = [];
			this.apiError = null;

			try {
				const { data } = await api.bulkImport.post({ fileId, jobId });

				if (data.success) {
					this.importedCount =
						data.summary?.successful || data.items?.length || 0;
					this.stepperStep = 3;
				} else {
					this.handleErrorResponse(data);
				}
			} catch (error) {
				// axios error structure can vary - check both locations
				const errorData = error.response?.data || error.data;

				if (errorData && errorData.message) {
					this.handleErrorResponse(errorData);
				} else {
					this.apiError = this.$t('err.unexpected-error');
				}
			}
		},
		handleErrorResponse(data) {
			if (data.message === 'validation-errors' && data.errors) {
				this.validationErrors = data.errors;
			} else if (data.message === 'too-many-candidates') {
				// Use localized error from backend if available
				this.apiError = data.error || this.$t('err.too-many-candidates');
			} else if (
				data.message === 'maximum-candidate-upload-limit-reached-for-package'
			) {
				this.apiError = this.$t('err.package-limit-reached', {
					max: data.max_candidate_upload_allowed,
					current: data.uploaded_candidates_count,
				});
			} else {
				this.apiError = this.getErrorMessage(data.message);
			}
		},
		getErrorMessage(code) {
			const errorMessages = {
				'file-id-required': this.$t('err.file-id-required'),
				'job-id-required': this.$t('err.job-id-required'),
				'file-not-found': this.$t('err.file-not-found'),
				'file-parse-error': this.$t('err.file-parse-error'),
				'no-valid-candidates': this.$t('err.no-valid-candidates'),
				'pipeline-stage-not-found': this.$t('err.pipeline-stage-not-found'),
				'all-imports-failed': this.$t('err.all-imports-failed'),
				'insufficient-permissions': this.$t('err.insufficient-permissions'),
			};
			return errorMessages[code] || this.$t('err.unexpected-error');
		},
		async handleGoToSecondStep(evt) {
			if (evt) {
				evt.preventDefault();
			}

			const isValid = await this.$refs.bulk_import_form_step_1.validate();

			if (isValid) {
				this.resetState();
				this.stepperStep = 2;
			}
		},
	},
	created() {
		this.$store.dispatch('jobs/get', { updateStore: true });
	},
	mounted() {
		setTimeout(
			function () {
				this.isVisible = true;
			}.bind(this),
			250,
		);
	},
};
</script>

<style scoped>
.mt-4 {
	margin-top: 16px;
}
.mb-2 {
	margin-bottom: 8px;
}
.mb-3 {
	margin-bottom: 12px;
}
.pl-4 {
	padding-left: 16px;
}
</style>
